import { aiRequestInit, aiTarget, isServerRefusal } from '@/capture/AiGateway';
import { promptCacheKey } from '@shared/ai/caching';
import { defaultModelFor, type AiOperation } from '@shared/ai/models';

/**
 * The transport for every vision call the reconstruction makes.
 *
 * Uses the **Responses API**, which is where the current frontier models are served. Reading a
 * photograph well — recovering a scale anchor, dating the season off foliage, deriving the sun from
 * shadow geometry — is the hardest single judgement in this feature, so the flagship tier is the
 * right default even though it is the expensive one: stage 1 is made once per site, and everything
 * downstream inherits its errors.
 *
 * Everything about *talking to the API* lives here exactly once — encoding images, JSON mode,
 * truncation, HTTP failure translation. Everything about interpreting a reply lives with the schema
 * that reply belongs to: `SitePipeline` for the five stages, `ReferenceTagSuggest` for the tagging
 * pre-pass.
 *
 * Like `EnhanceClient`, the key goes straight from the browser to `api.openai.com`. That is a
 * deliberate trade for a local single-user tool with no backend, and the same caveat applies: the
 * key is readable from the page, so this is not a public-deployment pattern.
 */

/**
 * One call's two messages.
 *
 * Lives with the transport rather than with any one prompt builder, because five stage prompts and
 * the tagging pre-pass all produce the same pair and all hand it to the same function.
 */
export interface ReferencePrompt {
  system: string;
  user: string;
}

export interface SitePlanModelOption {
  id: string;
  label: string;
  /** Shown under the dropdown so the choice is a judgement rather than a guess. */
  note: string;
}

/**
 * Vision models offered in the dialog, most capable first.
 *
 * A curated list rather than a live `/v1/models` query: that endpoint returns every embedding and
 * audio model on the account, and filtering it to "things that can read a photograph and emit JSON"
 * is guesswork from the client side. Anything not listed can still be typed in — see "Other".
 */
export const SITE_PLAN_MODELS: readonly SitePlanModelOption[] = [
  {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 Sol',
    note: 'Flagship. Best at the hard part — scale, season, and sun from one photograph.',
  },
  {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 Terra',
    note: 'Balances intelligence and cost. Good for a straightforward site.',
  },
  {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    note: 'Cost-sensitive tier. Expect a rougher read of scale and season.',
  },
  {
    id: 'gpt-5.5',
    label: 'GPT-5.5',
    note: 'Previous generation. Use if your key has no GPT-5.6 access.',
  },
];

/**
 * The flagship: stage 1 is read once per site, and every later judgement inherits its errors.
 *
 * Read from the shared registry rather than written here, so the proxy's allow-list and this
 * default cannot disagree — which is exactly how the wand came to be refused in hosted mode.
 */
export const DEFAULT_SITE_PLAN_MODEL = defaultModelFor('site-plan');

/** Ceiling on the reply when a caller names none. Every stage raises it to what it actually needs. */
const MAX_OUTPUT_TOKENS = 8000;

/** Reference images larger than this are rejected before upload rather than after a slow failure. */
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

export const MAX_IMAGES = 6;

/** An API failure translated into something a user can act on. */
export class SitePlanError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'SitePlanError';
  }
}

/** What one vision call returns, before anything tries to interpret it as a plan. */
export interface VisionCallResult {
  /** The assistant's text, exactly as received. Kept so the UI can show what actually came back. */
  raw: string;
  model: string;
  usage?: { promptTokens: number; completionTokens: number };
}

export interface VisionCallRequest {
  apiKey: string;
  prompt: ReferencePrompt;
  /** Reference photographs. At least one; at most {@link MAX_IMAGES}. */
  images: readonly Blob[];
  model?: string;
  signal?: AbortSignal;
  /** Raised for a stage that legitimately produces more output than a single plan. */
  maxOutputTokens?: number;
  /**
   * Permit a call with no images at all.
   *
   * Reconstruction stages have nothing to say without a photograph, so the empty-image guard is a
   * real check for them. The wand is the opposite: most requests are typed against a site the model
   * is already given in text, and attaching a photograph is the exception. Opt-in rather than a
   * relaxed default, so the reconstruction path keeps the guard it needs.
   */
  allowTextOnly?: boolean;
  /**
   * Let the model search the web before answering.
   *
   * Off by default and deliberately so: every reconstruction stage is a judgement about *this*
   * photograph, and a model that goes looking for what a driveway usually looks like will answer
   * with the average of the internet instead of what it can see. The texture forge is the opposite
   * case — "red gravel" has a real answer out there, in the form of actual products with actual
   * colours, and inventing one from the name is exactly the failure to avoid.
   */
  webSearch?: boolean;
  /**
   * Which feature is making this call.
   *
   * Every one of these is a `/v1/responses` request and they are indistinguishable on the wire, so
   * the operation is what separates a wand cast from a reconstruction stage from the tagging
   * pre-pass. It picks the proxy path the server meters under, and — when the caller names no model
   * — the default that operation runs on. Defaults to the reconstruction, which is the caller that
   * was here first and the one for which the flagship is the right answer.
   */
  operation?: AiOperation;
  /** Injected for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * One JSON-mode vision call against the Responses API.
 *
 * The single entry point every caller uses: the pipeline makes it five times with five different
 * prompts and five different result shapes, and the tagging pre-pass makes it once more.
 */
export async function callVisionModel(request: VisionCallRequest): Promise<VisionCallResult> {
  const doFetch = request.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== 'function') {
    throw new SitePlanError('No fetch implementation is available in this environment.', null, false);
  }
  if (request.images.length === 0 && request.allowTextOnly !== true) {
    throw new SitePlanError('Add at least one reference photograph.', null, false);
  }
  if (request.images.length > MAX_IMAGES) {
    throw new SitePlanError(`At most ${MAX_IMAGES} reference images can be sent at once.`, null, false);
  }

  const operation: AiOperation = request.operation ?? 'site-plan';
  const model = request.model?.trim() || defaultModelFor(operation);
  const imageParts = await Promise.all(request.images.map(toImagePart));

  const body = {
    model,
    max_output_tokens: request.maxOutputTokens ?? MAX_OUTPUT_TOKENS,
    text: { format: { type: 'json_object' } },
    // Pins one prompt family to one cache rather than letting a busy account's calls scatter across
    // machines. Carries the operation and nothing about the user — see `shared/ai/caching.ts`.
    prompt_cache_key: promptCacheKey(operation),
    ...(request.webSearch === true ? { tools: [{ type: 'web_search' }] } : {}),
    input: [
      { role: 'system', content: [{ type: 'input_text', text: request.prompt.system }] },
      {
        // Images first. They are the same bytes for every stage of a reconstruction, so putting the
        // stage's text ahead of them would leave several thousand identical tokens sitting behind a
        // difference, where the discount cannot reach them. The text still says "the attached
        // photograph"; nothing about that sentence requires it to be read first.
        role: 'user',
        content: [...imageParts, { type: 'input_text', text: request.prompt.user }],
      },
    ],
  };

  const target = aiTarget(operation, request.apiKey);
  let response: Response;
  try {
    response = await doFetch(target.url, {
      method: 'POST',
      ...aiRequestInit(target, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      ...(request.signal ? { signal: request.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new SitePlanError('Could not reach the OpenAI API. Check your network connection.', null, true);
  }

  if (!response.ok) throw await describeFailure(response);

  const payload = (await response.json()) as ResponsesPayload;

  if (payload.status === 'incomplete') {
    // Silently parsing a truncated reply would drop whole planting masses without saying so.
    throw new SitePlanError(
      payload.incomplete_details?.reason === 'max_output_tokens'
        ? 'The model ran out of room before finishing. Try fewer reference images, or ask for a simpler site in the extra direction.'
        : 'The model stopped before finishing. Retrying usually works.',
      response.status,
      true,
    );
  }

  const raw = readOutputText(payload);
  if (raw.trim().length === 0) {
    throw new SitePlanError('The model returned an empty response.', response.status, true);
  }

  const result: VisionCallResult = { raw, model };
  const usage = payload.usage;
  if (usage) {
    result.usage = {
      promptTokens: usage.input_tokens ?? 0,
      completionTokens: usage.output_tokens ?? 0,
    };
  }
  return result;
}

interface ResponsesPayload {
  status?: string;
  incomplete_details?: { reason?: string };
  output_text?: string;
  output?: { content?: { type?: string; text?: string }[] }[];
  usage?: { input_tokens?: number; output_tokens?: number };
}

/**
 * Pull the assistant's text out of a Responses payload.
 *
 * `output_text` is the convenience field the SDKs surface, but it is not guaranteed on the raw HTTP
 * body, so the `output[].content[]` walk is the real path and the convenience field is the shortcut.
 */
export function readOutputText(payload: ResponsesPayload): string {
  if (typeof payload.output_text === 'string' && payload.output_text.length > 0) {
    return payload.output_text;
  }
  const parts: string[] = [];
  for (const item of payload.output ?? []) {
    for (const content of item.content ?? []) {
      if (content.type === 'output_text' && typeof content.text === 'string') parts.push(content.text);
    }
  }
  return parts.join('');
}

/**
 * Pull the JSON object out of a reply.
 *
 * `json_object` mode should make this a no-op, but a model asked for JSON still sometimes wraps it
 * in a markdown fence, and failing the whole run over three backticks would be a poor trade.
 */
export function extractJson(content: string): string {
  const trimmed = content.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/.exec(trimmed);
  if (fenced?.[1]) return fenced[1].trim();

  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start >= 0 && end > start) return trimmed.slice(start, end + 1);
  return trimmed;
}

/** Encode a reference image as a Responses API input part. */
async function toImagePart(blob: Blob): Promise<{ type: 'input_image'; image_url: string; detail: 'high' }> {
  if (blob.size > MAX_IMAGE_BYTES) {
    throw new SitePlanError(
      `A reference image is larger than ${Math.round(MAX_IMAGE_BYTES / 1024 / 1024)} MB. Downscale it and try again.`,
      null,
      false,
    );
  }
  const base64 = await blobToBase64(blob);
  const mime = blob.type || 'image/png';
  // "high" detail is the point of the exercise: at low detail the model cannot resolve a shed door
  // or a shadow edge, and the scale anchor is the first thing that fails.
  return { type: 'input_image', image_url: `data:${mime};base64,${base64}`, detail: 'high' };
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked to avoid blowing the argument limit on a multi-megabyte photograph.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function describeFailure(response: Response): Promise<SitePlanError> {
  const { detail, code } = await readErrorDetail(response);

  // Being refused partway through a reconstruction is the one failure that is not a fault. The
  // server's message already names the state and the ways out of it, so it is passed through whole;
  // the generic 403 and 429 lines below would replace it with advice about API-key permissions or
  // billing at platform.openai.com, which on a hosted deployment is an account the user does not
  // have. `isServerRefusal` covers running out of credits, the cost cap, a suspended account, and
  // the deployment-wide kill switch — see `AiGateway`.
  if (isServerRefusal(code) && detail !== null) {
    return new SitePlanError(detail, response.status, false);
  }

  switch (response.status) {
    case 401:
      return new SitePlanError('OpenAI rejected the API key. Check that it is correct and still active.', 401, false);
    case 403:
      return new SitePlanError(
        "This API key is not permitted to use this model. Verify the key's project has it enabled.",
        403,
        false,
      );
    case 404:
      // The API's own message names the model; ours names the fix. Both are worth having, and a
      // 404 here is nearly always a mistyped or unavailable model rather than a broken request.
      return new SitePlanError(
        `${detail ?? 'That model was not found.'} Check the model name — it must be one your key can use, and it must accept images.`,
        404,
        false,
      );
    case 429:
      return new SitePlanError(
        'Rate limited or out of quota. Wait a moment and retry, or check your billing at platform.openai.com.',
        429,
        true,
      );
    case 400:
      return new SitePlanError(
        detail ?? 'The request was rejected. The reference images may be too large or in an unsupported format.',
        400,
        false,
      );
    default:
      break;
  }

  if (response.status >= 500) {
    return new SitePlanError('OpenAI had a server error. Retrying usually works.', response.status, true);
  }
  return new SitePlanError(detail ?? `Request failed (HTTP ${response.status}).`, response.status, false);
}

/**
 * The message and the machine-readable reason out of an error body.
 *
 * One shape covers both upstreams by luck rather than design: OpenAI answers `{ error: { message,
 * code } }` and so does this app's own proxy (see `shared/api.ts`), which matters because the same
 * client talks to whichever one the deployment put in front of it. The `code` is what lets a
 * refusal be told apart from a rate limit, which a status of 429 alone cannot do.
 */
async function readErrorDetail(response: Response): Promise<{
  detail: string | null;
  code: string | null;
}> {
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    const message = body.error?.message;
    const code = body.error?.code;
    return {
      detail: typeof message === 'string' && message.length > 0 ? message : null,
      code: typeof code === 'string' && code.length > 0 ? code : null,
    };
  } catch {
    return { detail: null, code: null };
  }
}
